import { useState } from 'react';
import Link from 'next/link';
import UserAvatar, { useProfilePrefetchHandlers } from '../UserAvatar';
import { NotifIcon } from './notificationTypeStyles';
import MutualFriendsModal from './MutualFriendsModal';
import MutualFriendsStack from './MutualFriendsStack';

const ACCENT = '#3b82f6';
const ACCENT_TEXT = '#7dd3fc';

function NameLink({ user, className = '', style, fallback = 'Someone', onClick }) {
  const name = user?.username || user?.name || fallback;
  const prefetch = useProfilePrefetchHandlers(user);
  if (user?.id) {
    const mergedClick = (e) => {
      prefetch.onClick?.(e);
      onClick?.(e);
    };
    return (
      <Link
        href={`/profile/${user.id}`}
        className={`sm:hover:underline sm:hover:text-sky-200 transition-colors ${className}`}
        style={style}
        {...prefetch}
        onClick={mergedClick}
      >
        {name}
      </Link>
    );
  }
  return (
    <span className={className} style={style}>
      {name}
    </span>
  );
}

/**
 * Distinct friend-request card. Shared by the bell dropdown, the full
 * Notifications page, and the global toast so the look stays consistent
 * wherever a friend request appears. Breaks out of the shared `Row`/`TypedRow`
 * layout used by results / battle invites: bigger avatar, requester's
 * username as the focal point, solid high-contrast blue card, clear
 * "wants to be your friend" framing, and prominent Accept / Decline.
 */
function formatJoinedAgo(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffMs = Date.now() - t;
  if (diffMs < 0) return '';
  const day = 86400000;
  const days = Math.floor(diffMs / day);
  if (days < 1) return 'today';
  if (days < 7) return `${days}d ago`;
  if (days < 30) {
    const w = Math.floor(days / 7);
    return `${w}w ago`;
  }
  if (days < 365) {
    const mo = Math.floor(days / 30);
    return `${mo}mo ago`;
  }
  const y = Math.floor(days / 365);
  return `${y}y ago`;
}

/**
 * Picks the most useful single line of social proof to show under the
 * "wants to be your friend" subtitle. Mutual friends rank highest because
 * they're the most actionable signal, then prior battle history, then a
 * friendly "joined Piks <X> ago" fallback so the slot rarely looks empty.
 * Returns null when there's truly nothing to say so the card collapses
 * gracefully instead of rendering an awkward placeholder.
 */
function pickContextLine(context) {
  if (!context || typeof context !== 'object') return null;
  const mutual = Number(context.mutualFriends) || 0;
  const battles = Number(context.priorBattles) || 0;
  if (mutual > 0) {
    return mutual === 1 ? '1 mutual friend' : `${mutual} mutual friends`;
  }
  if (battles > 0) {
    return battles === 1
      ? 'Played 1 battle against you'
      : `Played ${battles} battles against you`;
  }
  if (context.joinedAt) {
    const ago = formatJoinedAgo(context.joinedAt);
    if (ago) return `Joined Piks ${ago}`;
  }
  return null;
}

export default function FriendRequestCard({
  sender,
  time,
  busy = false,
  onAccept,
  onDecline,
  onDismiss,
  compact = false,
  // Optional social-proof payload from /api/notifications. When present,
  // the card shows a small line of context (mutual friends, prior battles,
  // or join age) under "wants to be your friend".
  context = null,
  // When true, the entire card sits inside an outer container that already
  // has its own background/border (e.g. inside a list section). We drop the
  // outer rounded card chrome so adjacent rows don't get extra spacing.
  inset = false,
  // Optional callback fired when the user taps the avatar or username to
  // navigate to the requester's profile. Surfaces that overlay the page
  // (bell dropdown, global toast) wire this to their own close/dismiss
  // handler so the surface gets out of the way once the user has committed
  // to viewing the profile. Not wired on the full /notifications page,
  // where a normal client-side navigation is the expected behavior.
  onProfileNavigate,
}) {
  const contextLine = pickContextLine(context);
  const mutualPreview = Array.isArray(context?.mutualFriendPreview)
    ? context.mutualFriendPreview
    : [];
  const mutualCount = Number(context?.mutualFriends) || 0;
  const showMutualStack = mutualCount > 0 && mutualPreview.length > 0;
  // Allow opening the full mutual-friends list whenever there's at least one
  // mutual — even small lists are worth seeing in full ("oh I know all 2 of
  // them"), and it keeps the affordance consistent across cards.
  const canSeeAllMutuals = mutualCount > 0 && !!sender?.id;
  const [mutualOpen, setMutualOpen] = useState(false);
  const openMutualList = () => { if (canSeeAllMutuals) setMutualOpen(true); };
  const closeMutualList = () => setMutualOpen(false);
  const avatarSize = compact ? 52 : 60;
  const stackAvatarSize = compact ? 16 : 18;
  const containerCls = inset
    ? 'relative px-4 py-3'
    : 'relative rounded-xl p-3.5 sm:p-4';
  const containerStyle = inset
    ? {
        background:
          'linear-gradient(135deg, rgba(59,130,246,0.18) 0%, rgba(59,130,246,0.05) 100%)',
        borderLeft: `3px solid ${ACCENT}`,
        boxShadow: 'inset 0 0 24px rgba(59,130,246,0.10)',
      }
    : {
        // Solid, opaque dark background so the card reads clearly over the
        // page (the prior translucent fill + blur made it nearly invisible).
        background: 'linear-gradient(135deg, #0f2a4a 0%, #0a1929 100%)',
        border: '1px solid rgba(59,130,246,0.6)',
        boxShadow:
          '0 0 0 1px rgba(59,130,246,0.15), 0 10px 32px -10px rgba(0,0,0,0.7)',
      };

  return (
    <div className={containerCls} style={containerStyle}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5"
          style={{
            color: ACCENT_TEXT,
            backgroundColor: 'rgba(59,130,246,0.18)',
            border: '1px solid rgba(59,130,246,0.5)',
          }}
        >
          <NotifIcon name="userPlus" size={10} color={ACCENT_TEXT} strokeWidth={2.5} />
          Friend Request
        </span>
        <div className="flex items-center gap-2">
          {time && (
            <span className="text-[10px] text-sky-200/70 flex-shrink-0">
              {time}
            </span>
          )}
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="text-sky-200/70 sm:hover:text-white text-xl leading-none px-1"
              aria-label="Dismiss"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">
          <UserAvatar
            user={sender}
            frameId={sender?.equippedFrame}
            size={avatarSize}
            link
            onLinkClick={onProfileNavigate}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white text-base sm:text-lg font-extrabold truncate leading-tight">
            <NameLink
              user={sender}
              fallback="Someone"
              onClick={onProfileNavigate}
            />
          </div>
          <div className="text-sky-100/85 text-xs sm:text-sm mt-0.5">
            wants to be your friend
          </div>
          {contextLine && (
            <div
              className="flex items-center gap-1.5 text-[11px] sm:text-xs mt-0.5 min-w-0"
              style={{ color: 'rgba(186,230,253,0.85)' }}
            >
              {showMutualStack && (
                <MutualFriendsStack
                  preview={mutualPreview}
                  size={stackAvatarSize}
                  total={mutualCount}
                  onProfileNavigate={onProfileNavigate}
                  onSeeAll={canSeeAllMutuals ? openMutualList : undefined}
                />
              )}
              {canSeeAllMutuals ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openMutualList();
                  }}
                  className="truncate text-left sm:hover:underline focus:outline-none focus-visible:underline"
                  style={{ color: 'rgba(186,230,253,0.95)' }}
                  aria-label={`See all ${mutualCount} mutual ${mutualCount === 1 ? 'friend' : 'friends'}`}
                >
                  {contextLine}
                </button>
              ) : (
                <span className="truncate">{contextLine}</span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 mt-3">
        <button
          type="button"
          disabled={busy}
          onClick={onAccept}
          className="flex-1 bg-blue-500 sm:hover:bg-blue-400 text-white text-sm font-bold py-2 rounded-lg disabled:opacity-50 transition-colors"
          style={{ boxShadow: '0 0 12px rgba(59,130,246,0.55)' }}
        >
          Accept
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onDecline}
          className="flex-1 text-sm font-medium py-2 rounded-lg disabled:opacity-50 text-sky-100 transition-colors"
          style={{
            backgroundColor: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(59,130,246,0.35)',
          }}
        >
          Decline
        </button>
      </div>

      {canSeeAllMutuals && (
        <MutualFriendsModal
          isOpen={mutualOpen}
          onClose={closeMutualList}
          senderId={sender?.id}
          senderUsername={sender?.username}
          expectedCount={mutualCount}
          onProfileNavigate={onProfileNavigate}
        />
      )}
    </div>
  );
}
